import { useEffect, useMemo, useRef, useState } from "react";
import {
  completeReview,
  loadOrCreateReviewDraft,
  saveReviewDraft,
} from "../application/round02-service";
import type { CEOOSDatabase } from "../db/database";
import type { ReviewDraftData, ReviewStatementDraft } from "../domain/review";
import { reviewIssueLabel } from "../domain/review-labels";
import type {
  BehaviorCorrection,
  CEOProfile,
  DailyCEOReview,
  EvidenceStatement,
  ReviewMode,
} from "../domain/types";
import { safeErrorMessage } from "../privacy/policy";
import { validateConcreteCorrection, synthesizeReview } from "../rules/review-synthesis";
import { BackIcon } from "../ui/icons";

type ReviewCompletion = {
  review: DailyCEOReview;
  correction: BehaviorCorrection;
  statements: EvidenceStatement[];
};

const QUICK_STEPS = ["真相", "偏差", "纠偏", "状态", "陈述确认", "结果"] as const;
const FULL_STEPS = [
  "真相",
  "聚焦",
  "重复",
  "信任",
  "决策",
  "组织",
  "场域",
  "能量",
  "核心结果",
  "陈述确认",
  "结果",
] as const;

const FIELD_QUESTIONS = {
  shenzhen: "今天我把什么不确定性变成了可验证结果？",
  hong_kong: "今天我增加了什么信任、准确性、合规性或可托付性？",
  gba: "今天我消除了哪一项跨境摩擦？",
  custom: "今天我在当前场域中修正了什么可观察行为？",
} as const;

export function ReviewFlow({
  database,
  profile,
  dateKey,
  onBack,
  onComplete,
}: {
  database: CEOOSDatabase;
  profile: CEOProfile;
  dateKey: string;
  onBack: () => void;
  onComplete: (result: ReviewCompletion) => void;
}) {
  const [draft, setDraft] = useState<ReviewDraftData>();
  const [saveState, setSaveState] = useState("加载草稿…");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const persistedDraftRef = useRef("");

  useEffect(() => {
    void loadOrCreateReviewDraft(database, dateKey, "quick")
      .then((loaded) => {
        persistedDraftRef.current = JSON.stringify({ ...loaded, lastSavedAt: "" });
        setDraft(loaded);
        setSaveState(
          loaded.lastSavedAt === loaded.startedAt
            ? "新草稿 · 仅保存在此设备"
            : `已恢复 · ${new Date(loaded.lastSavedAt).toLocaleTimeString("zh-CN", {
                hour: "2-digit",
                minute: "2-digit",
              })}`,
        );
      })
      .catch((error) => setNotice(safeErrorMessage(error)));
  }, [database, dateKey]);

  useEffect(() => {
    if (!draft) return;
    const persistenceKey = JSON.stringify({ ...draft, lastSavedAt: "" });
    if (persistenceKey === persistedDraftRef.current) return;
    setSaveState("保存中…");
    const timer = window.setTimeout(() => {
      void saveReviewDraft(database, draft)
        .then((saved) => {
          persistedDraftRef.current = persistenceKey;
          setDraft((current) =>
            current?.lastSavedAt === draft.lastSavedAt
              ? { ...current, lastSavedAt: saved.lastSavedAt }
              : current,
          );
          setSaveState(
            `已自动保存 · ${new Date(saved.lastSavedAt).toLocaleTimeString("zh-CN", {
              hour: "2-digit",
              minute: "2-digit",
            })}`,
          );
        })
        .catch(() => setSaveState("自动保存失败，请勿关闭页面"));
    }, 500);
    return () => window.clearTimeout(timer);
  }, [database, draft]);

  const steps = draft?.mode === "full" ? FULL_STEPS : QUICK_STEPS;
  const stepName = steps[draft?.step ?? 0];
  const synthesis = useMemo(
    () =>
      draft ? synthesizeReview(draft.answers, { existingStatements: draft.statements }) : undefined,
    [draft],
  );

  if (!draft) {
    return (
      <main className="review-page">
        <div className="inline-notice" role="status">
          {notice || "正在读取本地草稿…"}
        </div>
      </main>
    );
  }

  const updateAnswer = <K extends keyof ReviewDraftData["answers"]>(
    key: K,
    value: ReviewDraftData["answers"][K],
  ) => {
    setDraft({
      ...draft,
      answers: { ...draft.answers, [key]: value },
    });
    setNotice("");
  };

  const updateStatement = (id: string, patch: Partial<ReviewStatementDraft>) => {
    setDraft({
      ...draft,
      statements: draft.statements.map((statement) =>
        statement.id === id ? { ...statement, ...patch } : statement,
      ),
    });
  };

  const toggleNotApplicable = (key: keyof ReviewDraftData["answers"]) => {
    const currentlySkipped = draft.notApplicable.includes(key);
    setDraft({
      ...draft,
      answers: currentlySkipped ? draft.answers : { ...draft.answers, [key]: "" },
      notApplicable: currentlySkipped
        ? draft.notApplicable.filter((item) => item !== key)
        : [...draft.notApplicable, key],
    });
  };

  const validateCurrentStep = (): string | undefined => {
    if (stepName === "真相" && draft.answers.uncomfortableTruth.trim().length < 4) {
      return "请记录一个不舒服的事实；证据不足时可以直接写“数据不足”。";
    }
    if (
      (stepName === "偏差" || stepName === "核心结果") &&
      (draft.answers.founderDeviation.trim().length < 4 ||
        draft.answers.affectedBusinessResult.trim().length < 4 ||
        !draft.answers.issueType)
    ) {
      return "请只选一个偏差，说明受影响的业务结果，并选择问题类型。";
    }
    if (stepName === "纠偏" || stepName === "核心结果") {
      const issue = validateConcreteCorrection(
        draft.answers.correctionAction,
        draft.answers.verificationMetric,
      )[0];
      if (issue) return issue.message;
    }
    if (stepName === "陈述确认") {
      const accepted = draft.statements.filter(
        (statement) =>
          statement.userConfirmation === "confirmed" || statement.userConfirmation === "corrected",
      );
      if (accepted.length === 0) return "至少确认一条陈述，或返回修改原始回答。";
      const unreviewed = draft.statements.some(
        (statement) => statement.userConfirmation === "unconfirmed",
      );
      if (unreviewed) return "请确认、修正或拒绝每条陈述，避免默认把输入当成事实。";
      const inferenceWithoutFact =
        accepted.some((statement) => statement.statementType === "inference") &&
        !accepted.some((statement) => statement.statementType === "fact");
      if (inferenceWithoutFact) return "确认推断前，至少保留一条支持它的事实。";
    }
    return undefined;
  };

  const moveNext = () => {
    const error = validateCurrentStep();
    if (error) {
      setNotice(error);
      return;
    }
    const nextStep = Math.min(draft.step + 1, steps.length - 1);
    const nextName = steps[nextStep];
    const statements =
      nextName === "陈述确认"
        ? synthesizeReview(draft.answers, {
            existingStatements: draft.statements,
          }).statements
        : draft.statements;
    setDraft({ ...draft, step: nextStep, statements });
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const changeMode = (mode: ReviewMode) => {
    if (mode === draft.mode) return;
    setDraft((current) => {
      if (!current || current.mode === mode) return current;
      const currentSteps = current.mode === "full" ? FULL_STEPS : QUICK_STEPS;
      const nextSteps = mode === "full" ? FULL_STEPS : QUICK_STEPS;
      const currentName = currentSteps[current.step];
      const semanticTarget =
        currentName === "偏差" || currentName === "纠偏"
          ? "核心结果"
          : currentName === "状态"
            ? "能量"
            : currentName === "核心结果"
              ? "偏差"
              : currentName === "能量"
                ? "状态"
                : currentName;
      const mappedStep = nextSteps.findIndex((name) => name === semanticTarget);
      return { ...current, mode, step: mappedStep >= 0 ? mappedStep : 0 };
    });
    setNotice("模式已切换；已经填写的内容不会丢失。");
  };

  const finishReview = async () => {
    const error = validateCurrentStep();
    if (error) {
      setNotice(error);
      return;
    }
    setSubmitting(true);
    try {
      const saved = await saveReviewDraft(database, draft);
      const result = await completeReview(database, profile, saved);
      onComplete(result);
    } catch (errorValue) {
      setNotice(safeErrorMessage(errorValue));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="review-page">
      <header className="review-topbar">
        <button type="button" className="back-button" onClick={onBack}>
          <BackIcon />
          今天
        </button>
        <h1>今日复盘</h1>
        <span />
      </header>

      <div className="mode-switch" role="group" aria-label="复盘模式">
        <button
          type="button"
          className={draft.mode === "quick" ? "is-selected" : ""}
          onClick={() => changeMode("quick")}
        >
          快速 3—5 分钟
        </button>
        <button
          type="button"
          className={draft.mode === "full" ? "is-selected" : ""}
          onClick={() => changeMode("full")}
        >
          完整 8—12 分钟
        </button>
      </div>

      <div className="review-progress">
        <strong>
          {draft.step + 1} / {steps.length}
        </strong>
        <div className="progress-rail">
          <span style={{ width: `${((draft.step + 1) / steps.length) * 100}%` }} />
        </div>
        <div>
          <span>{saveState}</span>
          <small>仅保存在此设备</small>
        </div>
      </div>

      {notice && (
        <div className="inline-notice" role="alert">
          {notice}
        </div>
      )}

      <section className="review-step" aria-labelledby="review-step-title">
        <ReviewStepContent
          stepName={stepName}
          draft={draft}
          updateAnswer={updateAnswer}
          updateStatement={updateStatement}
          toggleNotApplicable={toggleNotApplicable}
          fieldQuestion={
            profile.defaultFieldMode === "custom"
              ? profile.customFieldQuestion || FIELD_QUESTIONS.custom
              : FIELD_QUESTIONS[profile.defaultFieldMode]
          }
          synthesis={synthesis}
        />
      </section>

      <footer className="review-actions">
        <button
          type="button"
          className="secondary-button"
          onClick={() => {
            if (draft.step === 0) onBack();
            else setDraft({ ...draft, step: draft.step - 1 });
          }}
        >
          {draft.step === 0 ? "退出并保存" : "上一步"}
        </button>
        {draft.step < steps.length - 1 ? (
          <button type="button" className="primary-button" onClick={moveNext}>
            下一步
          </button>
        ) : (
          <button
            type="button"
            className="primary-button"
            disabled={submitting}
            onClick={() => void finishReview()}
          >
            {submitting ? "正在确认…" : "确认纠偏并完成"}
          </button>
        )}
      </footer>
    </main>
  );
}

function ReviewStepContent({
  stepName,
  draft,
  updateAnswer,
  updateStatement,
  toggleNotApplicable,
  fieldQuestion,
  synthesis,
}: {
  stepName: string | undefined;
  draft: ReviewDraftData;
  updateAnswer: <K extends keyof ReviewDraftData["answers"]>(
    key: K,
    value: ReviewDraftData["answers"][K],
  ) => void;
  updateStatement: (id: string, patch: Partial<ReviewStatementDraft>) => void;
  toggleNotApplicable: (key: keyof ReviewDraftData["answers"]) => void;
  fieldQuestion: string;
  synthesis?: ReturnType<typeof synthesizeReview> | undefined;
}) {
  const textArea = (
    key: keyof ReviewDraftData["answers"],
    label: string,
    helper: string,
    rows = 5,
    allowNotApplicable = false,
  ) => (
    <div className="optional-answer">
      <label className="field">
        <span>{label}</span>
        <small>{helper}</small>
        <textarea
          aria-label={label}
          disabled={draft.notApplicable.includes(key)}
          rows={rows}
          maxLength={600}
          value={String(draft.answers[key] ?? "")}
          onChange={(event) => updateAnswer(key, event.target.value as never)}
        />
      </label>
      {allowNotApplicable && (
        <button
          type="button"
          className="not-applicable-button"
          aria-pressed={draft.notApplicable.includes(key)}
          onClick={() => toggleNotApplicable(key)}
        >
          {draft.notApplicable.includes(key) ? "已标记不适用 · 撤销" : "标记为不适用"}
        </button>
      )}
    </div>
  );

  switch (stepName) {
    case "真相":
      return (
        <>
          <h2 id="review-step-title">今天我是否回避了某个不舒服的事实？</h2>
          <p className="step-helper">写可观察的事实；不要先解释自己为什么这样做。</p>
          {textArea(
            "uncomfortableTruth",
            "今天最重要的不舒服事实",
            "证据不足时，直接写明哪些数据还不知道。",
          )}
          <details className="disclosure">
            <summary>查看辅助问题</summary>
            <ul>
              <li>哪项证据最不支持我当前的判断？</li>
              <li>有没有把忙碌或口头支持当成进展？</li>
            </ul>
          </details>
        </>
      );
    case "偏差":
    case "核心结果":
      return (
        <>
          <h2 id="review-step-title">今天最大的个人偏差是什么？</h2>
          <p className="step-helper">只描述一个影响经营的行为，不给自己贴人格标签。</p>
          {textArea(
            "founderDeviation",
            "一个具体行为偏差",
            "例如：继续优化系统结构，推迟了原定客户访谈。",
          )}
          {textArea(
            "affectedBusinessResult",
            "它影响了哪个业务结果？",
            "例如：有效付费访谈仍然不足。",
            3,
          )}
          <label className="field">
            <span>问题类型</span>
            <select
              aria-label="问题类型"
              value={draft.answers.issueType}
              onChange={(event) =>
                updateAnswer(
                  "issueType",
                  event.target.value as ReviewDraftData["answers"]["issueType"],
                )
              }
            >
              <option value="">请选择，不使用人格标签</option>
              <option value="direction_issue">方向问题</option>
              <option value="method_issue">方法问题</option>
              <option value="execution_issue">执行问题</option>
              <option value="random_factor">随机因素</option>
              <option value="insufficient_data">数据不足</option>
            </select>
          </label>
          {stepName === "核心结果" && (
            <>
              {textArea(
                "correctionAction",
                "明天唯一行为纠偏",
                "必须是可观察动作，不能写“更努力”或“保持专注”。",
                4,
              )}
              {textArea(
                "verificationMetric",
                "明天如何验证？",
                "写数量、结果或其他可观察证据。",
                3,
              )}
            </>
          )}
        </>
      );
    case "纠偏":
      return (
        <>
          <h2 id="review-step-title">明天只改哪一个行为？</h2>
          <p className="step-helper">动作要能执行、能观察、能在明天检查。</p>
          {textArea(
            "correctionAction",
            "明天唯一行为纠偏",
            "例如：开发任何系统前，先向 5 名有效 B 端合作方提出转介请求。",
          )}
          {textArea(
            "verificationMetric",
            "验证方式",
            "例如：记录 5 次请求、回复数和明确拒绝原因。",
            3,
          )}
        </>
      );
    case "状态":
    case "能量":
      return (
        <>
          <h2 id="review-step-title">身体和注意力是否影响了经营？</h2>
          <p className="step-helper">只记录经营相关状态，不做医学或心理诊断。</p>
          <fieldset className="energy-picker">
            <legend>今日综合能量</legend>
            {[1, 2, 3, 4, 5].map((value) => (
              <label key={value}>
                <input
                  type="radio"
                  name="energy"
                  checked={draft.answers.energyLevel === value}
                  onChange={() => updateAnswer("energyLevel", value as 1 | 2 | 3 | 4 | 5)}
                />
                <span>{value}</span>
              </label>
            ))}
          </fieldset>
          {draft.mode === "quick" &&
            textArea("fieldAnswer", fieldQuestion, "一行即可；场域问题不会替你生成结论。", 4)}
          <details className="disclosure">
            <summary>如何判断</summary>
            <p>综合考虑睡眠感受、注意力、体力、情绪稳定和是否明显透支。</p>
          </details>
        </>
      );
    case "聚焦":
      return (
        <>
          <h2 id="review-step-title">有没有让主线外的事情抢走资源？</h2>
          {textArea(
            "focus",
            "今天的聚焦偏差（可跳过）",
            "新想法只做轻量捕获，不在这里立项。",
            5,
            true,
          )}
        </>
      );
    case "重复":
      return (
        <>
          <h2 id="review-step-title">是否完成了最重要但枯燥的重复动作？</h2>
          {textArea(
            "repetition",
            "重复与执行耐力（可跳过）",
            "有没有用研究、材料或系统建设逃避市场动作？",
            5,
            true,
          )}
        </>
      );
    case "信任":
      return (
        <>
          <h2 id="review-step-title">今天是否兑现承诺并增加了信任？</h2>
          {textArea(
            "trust",
            "信任偏差（可跳过）",
            "只记录承诺和准确性，不建立完整 Promise Ledger。",
            5,
            true,
          )}
        </>
      );
    case "决策":
      return (
        <>
          <h2 id="review-step-title">今天最重要的判断，依据是否充分？</h2>
          {textArea(
            "decisionQuality",
            "决策质量摘要（可跳过）",
            "不要因为结果好坏直接倒推判断质量。",
            5,
            true,
          )}
        </>
      );
    case "组织":
      return (
        <>
          <h2 id="review-step-title">哪件事不应该继续由我亲自完成？</h2>
          {textArea(
            "organization",
            "组织与授权候选（可跳过）",
            "只捕获候选，不提前建设瓶颈管理页。",
            5,
            true,
          )}
        </>
      );
    case "场域":
      return (
        <>
          <h2 id="review-step-title">{fieldQuestion}</h2>
          {textArea("fieldAnswer", "场域回答（可跳过）", "描述今天发生的具体行为或摩擦。", 5, true)}
        </>
      );
    case "陈述确认":
      return (
        <>
          <h2 id="review-step-title">确认陈述性质</h2>
          <p className="step-helper">
            原始叙述默认不是事实。逐条确认、修正或拒绝；这不是知识图谱编辑器。
          </p>
          <div className="statement-list">
            {draft.statements.map((statement) => (
              <div className="statement-row" key={statement.id}>
                <label className="field">
                  <span>内容</span>
                  <textarea
                    aria-label="内容"
                    rows={3}
                    value={statement.content}
                    onChange={(event) =>
                      updateStatement(statement.id, {
                        content: event.target.value,
                        userConfirmation: "corrected",
                      })
                    }
                  />
                </label>
                <div className="statement-controls">
                  <label>
                    <span>类型</span>
                    <select
                      aria-label="类型"
                      value={statement.statementType}
                      onChange={(event) =>
                        updateStatement(statement.id, {
                          statementType: event.target
                            .value as ReviewStatementDraft["statementType"],
                          userConfirmation: "corrected",
                        })
                      }
                    >
                      <option value="fact">事实</option>
                      <option value="inference">推断</option>
                      <option value="hypothesis">假设</option>
                      <option value="recommendation">建议</option>
                    </select>
                  </label>
                  <label>
                    <span>处理</span>
                    <select
                      aria-label="处理"
                      value={statement.userConfirmation}
                      onChange={(event) =>
                        updateStatement(statement.id, {
                          userConfirmation: event.target
                            .value as ReviewStatementDraft["userConfirmation"],
                        })
                      }
                    >
                      <option value="unconfirmed">尚未确认</option>
                      <option value="confirmed">确认</option>
                      <option value="corrected">修正后确认</option>
                      <option value="rejected">拒绝</option>
                    </select>
                  </label>
                </div>
                {statement.statementType === "hypothesis" && (
                  <div className="statement-controls">
                    <label>
                      <span>验证条件</span>
                      <input
                        aria-label="验证条件"
                        value={statement.verificationCondition ?? ""}
                        onChange={(event) =>
                          updateStatement(statement.id, {
                            verificationCondition: event.target.value,
                          })
                        }
                      />
                    </label>
                    <label>
                      <span>反证条件</span>
                      <input
                        aria-label="反证条件"
                        value={statement.falsificationCondition ?? ""}
                        onChange={(event) =>
                          updateStatement(statement.id, {
                            falsificationCondition: event.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      );
    case "结果":
      return (
        <>
          <h2 id="review-step-title">确认今天的复盘结果</h2>
          <p className="step-helper">综合结果只来自你已经填写和确认的内容，仍可返回修改。</p>
          <dl className="result-list">
            <div>
              <dt>不舒服事实</dt>
              <dd>{draft.answers.uncomfortableTruth}</dd>
            </div>
            <div>
              <dt>最大个人偏差</dt>
              <dd>{draft.answers.founderDeviation}</dd>
            </div>
            <div>
              <dt>受影响业务结果</dt>
              <dd>{draft.answers.affectedBusinessResult}</dd>
            </div>
            <div>
              <dt>问题类型</dt>
              <dd>{reviewIssueLabel(synthesis?.issueType)}</dd>
            </div>
            <div className="result-highlight">
              <dt>明日唯一行为纠偏</dt>
              <dd>{draft.answers.correctionAction}</dd>
            </div>
            <div>
              <dt>验证方式</dt>
              <dd>{draft.answers.verificationMetric}</dd>
            </div>
          </dl>
          <p className="privacy-footnote">
            点击确认后才会激活纠偏；系统不会自动替你确认，也不会生成综合 CEO 得分。
          </p>
        </>
      );
    default:
      return null;
  }
}

export type { ReviewCompletion };
